import Text from "@core/text";

import { trackEvent } from "@packages/hooks/useAnalytics";
import { useAppDispatch } from "@packages/store/hooks";
import {
  getCurrentTeacher,
  toggleFavoriteTool,
} from "@packages/store/userSlice";
import { ArrowRight, Flame, LucideIcon } from "lucide-react";
import { useState } from "react";
import { FavoriteButton } from "@features/tools/components/favorite-button";
import { toolsList } from "@packages/utils";

import { getToolColorFromCategory, ToolId } from "@packages/utils";
interface ActionItem {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  onClick: () => void;
  color: string;
  disabled: boolean;
  isNew?: boolean;
  isFavorite?: boolean;
  canBeFavorite?: boolean;
}

const RecentlyAddedItem = ({ action }: { action: ActionItem }) => {
  const { trackEvent: track } = trackEvent();
  const dispatch = useAppDispatch();
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = () => {
    track(
      {
        category: "ContentClick",
        action: "dashboard.quick.action.click",
        label: "Dashboard Quick Action Click",
        nonInteraction: false,
      },
      {
        location: "/dashboard/click",
        element: "click-button",
      },
    );
    action.onClick();
  };

  const toggleFavorite = async () => {
    await dispatch(toggleFavoriteTool(action.id));
    dispatch(getCurrentTeacher());
  };

  // const gradientColors = [
  //   "from-violet-600 to-purple-600",
  //   "from-rose-600 to-pink-600",
  //   "from-cyan-600 to-cyan-500",
  //   "from-teal-600 to-emerald-600",
  //   "from-blue-600 to-sky-600",
  //   "from-indigo-500 to-indigo-600",
  //   "from-orange-600 to-orange-600",
  //   "from-sky-400 to-sky-600",
  //   "from-green-600 to-green-600",
  //   "from-orange-500 to-orange-600",
  //   "from-[#D98787] to-[#C77272]",
  //   "from-[#F87171] to-[#EF4444]",
  //   "from-[#BFA58A] to-[#A07E5E]",
  //   "from-[#60A5FA] to-[#06B6D4]",
  // ];

  // const toolGradientMap: Record<string, number> = {
  //   podcasts: 0,
  //   "interactive-material": 1,
  //   vocabularies: 2,
  //   summaries: 3,
  //   "text-leveler": 4,
  //   images: 5,
  //   connections: 6,
  //   hooks: 7,
  //   choiceBoards: 8,
  //   "classroom-management": 9,
  //   transferActivities: 10,
  //   projectbasedlearning: 11,
  //   collaborative: 12,
  //   "performance-task": 13,
  // };

  // Use the tool's ID to determine the gradient if available, otherwise fall back to index
  // const gradientIndex =
  //   action.id && toolGradientMap[action.id] !== undefined
  //     ? toolGradientMap[action.id]
  //     : index % gradientColors.length;

  // const currentGradient = gradientColors[gradientIndex];

  const currentGradient = getToolColorFromCategory(action.id as ToolId)?.light;

  return (
    <div
      className="relative group h-full flex"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Pulsing Outline Effect */}
      <div
        className={`absolute inset-0 bg-gradient-to-br ${currentGradient} rounded-xl opacity-20 
        animate-pulse blur-sm -m-0.5 transition-all duration-300 ${isHovered ? "scale-105" : "scale-100"}`}
      />

      {/* Main Card */}
      <div
        onClick={handleClick}
        className={`relative overflow-hidden rounded-xl border border-transparent 
          bg-gradient-to-br ${currentGradient} transition-all duration-300
          cursor-pointer w-full h-full flex flex-col ${isHovered ? "scale-[1.03] shadow-xl" : "shadow-md"}`}
      >
        {/* Decorative Elements - Subtle Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute right-0 top-0 w-24 h-24 bg-white/10 rounded-full -mr-8 -mt-8"></div>
          <div className="absolute left-0 bottom-0 w-16 h-16 bg-white/10 rounded-full -ml-5 -mb-5"></div>
        </div>

        {/* Content Container */}
        <div className="p-4 text-white flex flex-col h-full">
          {/* Top Row - Icon, Title, Badges and Buttons */}
          <div className="flex justify-between items-start mb-3">
            {/* Left side - Icon and Title */}
            <div className="flex space-x-3 items-center flex-1">
              <div className="bg-white/20 backdrop-blur-sm p-2 rounded-lg flex-shrink-0">
                <action.icon size={22} className="text-white" />
              </div>
              <Text
                variant="h3"
                weight="bold"
                size="base"
                className="text-white flex-1"
              >
                {action.title}
              </Text>
            </div>

            {/* Right side - Badges and Buttons */}
            <div className="flex space-x-1.5">
              <FavoriteButton
                isFavorite={action.isFavorite ?? false}
                onToggleFavorite={toggleFavorite}
              />
            </div>
          </div>

          {/* Description - Fixed height with overflow handling */}
          <div className="bg-white/10 backdrop-blur-sm p-2.5 rounded-lg mb-2.5 h-[80px] overflow-y-auto">
            <Text variant="p" size="sm" className="!text-white leading-normal">
              {action.description}
            </Text>
          </div>

          {/* Call to Action */}
          <div className="flex justify-between mt-auto">
            <div className="bg-white/20 backdrop-blur-sm py-0.5 px-2 rounded-full text-xs font-medium flex items-center">
              <Text variant="span" className="text-white">
                quickActions.new
              </Text>
              <Flame size={12} className="ml-1 text-orange-300" />
            </div>
            <div
              className={`bg-white/20 hover:bg-white/30 backdrop-blur-sm py-1 px-2.5 rounded-lg flex items-center transition-all duration-300 ${isHovered ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1"}`}
            >
              <Text variant="span" className="text-white">
                quickActions.try_it_now
              </Text>
              <ArrowRight size={12} className="ml-1" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecentlyAddedItem;
